import { useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtMontant, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { AssujettiCombobox } from "@/components/AssujettiCombobox";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, FileSearch, Loader2, FileDown, Mail } from "lucide-react";

const STATUT_STYLES = {
  "Apuré": "bg-emerald-50 text-emerald-700 ring-emerald-200",
  "Partiel": "bg-amber-50 text-amber-700 ring-amber-200",
  "Impayé": "bg-red-50 text-red-700 ring-red-200",
  "Aucune note": "bg-slate-100 text-slate-500 ring-slate-200",
};

function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}

export default function ExtraitCompte() {
  const { canWrite } = useAuth();
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadExtrait = async (a) => {
    setSelectedId(a.id);
    setSelectedLabel(`${a.code} — ${a.redevable}`);
    setLoading(true);
    try {
      const { data } = await api.get(`/assujettis/${a.id}/extrait`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  const a = data?.assujetti;

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post(`/assujettis/${selectedId}/extrait/email`);
      toast.success(r.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="extrait-page">
      <div className="no-print">
        <PageHeader title="Extrait de Compte" subtitle="Situation individuelle d'un assujetti : notes de perception, apurements et solde">
          {data && (
            <>
              <Button variant="outline" onClick={() => window.print()} data-testid="print-extrait-btn">
                <Printer className="mr-1.5 h-4 w-4" /> Imprimer l'extrait
              </Button>
              <Button onClick={() => window.open(`${API}/assujettis/${selectedId}/extrait/pdf`, "_blank")}
                className="bg-slate-900 hover:bg-slate-800" data-testid="pdf-extrait-btn">
                <FileDown className="mr-1.5 h-4 w-4" /> Télécharger PDF
              </Button>
              {canWrite && (
                <Button variant="outline" onClick={sendEmail} disabled={sending || !a?.email}
                  title={a?.email ? `Envoyer à ${a.email}` : "Aucun email enregistré pour cet assujetti"} data-testid="email-extrait-btn">
                  {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Envoyer par email
                </Button>
              )}
            </>
          )}
        </PageHeader>

        <Card className="mb-6 border-slate-200 p-4 shadow-sm">
          <div className="max-w-xl">
            <p className="mb-1.5 text-sm font-medium text-slate-700">Sélectionner un assujetti</p>
            <AssujettiCombobox
              value={selectedId}
              selectedLabel={selectedLabel}
              onSelect={loadExtrait}
              testId="extrait-assujetti-select"
            />
          </div>
        </Card>
      </div>

      {loading && (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      )}

      {!loading && !data && (
        <div className="flex flex-col items-center justify-center gap-2 py-20 text-center no-print">
          <FileSearch className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <p className="font-heading text-lg font-medium text-slate-600">Aucun assujetti sélectionné</p>
          <p className="max-w-sm text-sm text-slate-400">Choisissez un assujetti pour afficher son extrait de compte.</p>
        </div>
      )}

      {!loading && data && a && (
        <div className="print-area space-y-6">
          <div className="print-only mb-4 text-center">
            <h2 className="font-heading text-xl font-bold">Extrait de Compte</h2>
          </div>

          <Card className="border-slate-200 p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{a.redevable}</h3>
                <p className="font-mono text-xs text-slate-500">{a.code}</p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ${STATUT_STYLES[data.statut] || STATUT_STYLES["Aucune note"]}`}
                data-testid="extrait-statut"
              >
                Statut : {data.statut}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <InfoCell label="Acte Générateur" value={a.acte_generateur_nom} />
              <InfoCell label="Province" value={a.province} />
              <InfoCell label="Ville" value={a.ville} />
              <InfoCell label="Commune" value={a.commune} />
              <InfoCell label="Quartier" value={a.quartier} />
              <InfoCell label="Avenue" value={a.avenue} />
              <InfoCell label="Téléphone" value={a.telephone} />
              <InfoCell label="N° Impôt" value={a.numero_impot} />
            </div>
          </Card>

          {/* Totaux par monnaie */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {data.totals.length === 0 ? (
              <Card className="col-span-3 border-slate-200 p-5 text-center text-slate-400 shadow-sm">Aucun mouvement</Card>
            ) : data.totals.map((t) => (
              <Card key={t.monnaie} className="border-slate-200 p-5 shadow-sm" data-testid="extrait-total-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Solde en {t.monnaie}</p>
                <p className={`mt-2 font-mono text-2xl font-bold ${t.solde > 0.009 ? "text-red-600" : "text-emerald-600"}`}>
                  {fmtMontant(t.solde, t.monnaie)}
                </p>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Total débit</span><span className="font-mono text-amber-700">{fmtMontant(t.debit)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Total apuré</span><span className="font-mono text-emerald-700">{fmtMontant(t.apurement)}</span></div>
                </div>
              </Card>
            ))}
          </div>

          {/* Note de perception */}
          <Card className="border-slate-200 shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4"><h3 className="font-heading text-base font-semibold text-slate-800">Notes de Perception</h3></div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>N°</TableHead><TableHead>Taxation</TableHead><TableHead>Mois/Année</TableHead>
                    <TableHead>Date</TableHead><TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.notes_debit.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-6 text-center text-slate-400">Aucune note de perception</TableCell></TableRow>
                  ) : data.notes_debit.map((n) => (
                    <TableRow key={n.id} data-testid="extrait-debit-row">
                      <TableCell className="font-mono text-xs">{n.numero}</TableCell>
                      <TableCell className="font-mono text-xs">{n.note_taxation}</TableCell>
                      <TableCell>{n.mois} {n.annee}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(n.date)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMontant(n.montant, n.monnaie)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Apurements */}
          <Card className="border-slate-200 shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4"><h3 className="font-heading text-base font-semibold text-slate-800">Notes d'Apurement</h3></div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>N°</TableHead><TableHead>N° Perception</TableHead><TableHead>Mois</TableHead>
                    <TableHead>Date</TableHead><TableHead className="text-right">Montant apuré</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.apurements.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-6 text-center text-slate-400">Aucun apurement</TableCell></TableRow>
                  ) : data.apurements.map((p) => (
                    <TableRow key={p.id} data-testid="extrait-apurement-row">
                      <TableCell className="font-mono text-xs">{p.numero}</TableCell>
                      <TableCell className="font-mono text-xs">{p.numero_debit}</TableCell>
                      <TableCell>{p.mois}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(p.date)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{fmtMontant(p.montant_apurement, p.monnaie)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
